import { useMemo, useState } from 'react'
import type { ChoiceOption, DataBundle, FieldDef, Row } from '../types'
import { buildChoices } from '../utils/choices'
import { encodeList, parseList, parseMap } from '../utils/csv'

function normalizeOptions(options: FieldDef['options']): ChoiceOption[] {
  return (options ?? []).map((option) => typeof option === 'string' ? { value: option, label: option } : option)
}

export function FieldControl({ field, row, setRow, data, locked = false }: {
  field: FieldDef
  row: Row
  setRow: (row: Row) => void
  data: DataBundle
  locked?: boolean
}) {
  const [showHelp, setShowHelp] = useState(false)
  const value = row[field.key] ?? ''
  const choices = useMemo(
    () => field.dynamic ? buildChoices(field.dynamic, data, row) : normalizeOptions(field.options),
    [data, field.dynamic, field.options, row],
  )
  const update = (next: string) => setRow({ ...row, [field.key]: next })
  const disabled = locked || Boolean(field.lockedWhenPublished && ['Activo', 'Listo para usar'].includes(row.status || ''))
  const id = `field-${field.key}`

  const help = (
    <>
      <button type="button" className="help-button" onClick={() => setShowHelp((current) => !current)} aria-expanded={showHelp}>?</button>
      {showHelp && <div className="field-help-panel">
        <p>{field.help || 'Completa este campo usando una opción compatible con Google Sheets.'}</p>
        {field.correctExample && <p><strong>Ejemplo correcto:</strong> {field.correctExample}</p>}
        {field.incorrectExample && <p><strong>Evita:</strong> {field.incorrectExample}</p>}
        <a href="#academy">Abrir ayuda en la Academia</a>
      </div>}
    </>
  )

  if (field.type === 'boolean') return <label className="toggle-field"><input type="checkbox" checked={['true','1','sí','si'].includes(value.toLowerCase())} onChange={(event) => update(String(event.target.checked))} disabled={disabled}/><span>{field.label}</span>{help}</label>

  if (field.type === 'textarea') return <label htmlFor={id} className="field"><span>{field.label}{field.required && <b> *</b>}{help}</span><textarea id={id} value={value} onChange={(event) => update(event.target.value)} rows={5} disabled={disabled} placeholder={field.placeholder}/></label>

  if (field.type === 'number') return <label htmlFor={id} className="field"><span>{field.label}{field.required && <b> *</b>}{help}</span><input id={id} type="number" value={value} onChange={(event) => update(event.target.value)} disabled={disabled}/></label>

  if (field.type === 'date') return <label htmlFor={id} className="field"><span>{field.label}{help}</span><input id={id} type="date" value={value} onChange={(event) => update(event.target.value)} disabled={disabled}/></label>

  if (field.type === 'select') return <label htmlFor={id} className="field"><span>{field.label}{field.required && <b> *</b>}{help}</span><select id={id} value={value} disabled={disabled} onChange={(event) => {
    if (event.target.value === '__custom__') {
      const custom = window.prompt(`Escribe el nuevo valor para “${field.label}”.`)
      if (custom?.trim()) update(custom.trim())
      return
    }
    update(event.target.value)
  }}>
    <option value="">Seleccionar…</option>
    {choices.map((choice) => <option key={choice.value} value={choice.value} disabled={choice.disabled}>{choice.label}{choice.description ? ` — ${choice.description}` : ''}</option>)}
    {['units','topics','sourceTypes'].includes(field.dynamic || '') && <option value="__custom__">＋ Crear un valor nuevo…</option>}
  </select>{value && choices.find((choice) => choice.value === value)?.description && <small>{choices.find((choice) => choice.value === value)?.description}</small>}</label>

  if (field.type === 'multi-select') {
    const values = parseList(value)
    const format = field.listFormat || 'pipe'
    return <fieldset className="multi-select"><legend>{field.label}{field.required && ' *'}{help}</legend><div className="choice-grid">{choices.map((choice) => <label key={choice.value} title={choice.description}><input type="checkbox" checked={values.includes(choice.value)} disabled={disabled} onChange={(event) => {
      const next = event.target.checked ? [...values, choice.value] : values.filter((item) => item !== choice.value)
      update(encodeList(next, format))
    }}/><span>{choice.label}</span></label>)}</div></fieldset>
  }

  if (field.type === 'tags' || field.type === 'list') {
    const values = parseList(value)
    return <label className="field"><span>{field.label}{field.required && <b> *</b>}{help}</span><textarea value={values.join('\n')} rows={5} disabled={disabled} onChange={(event) => update(encodeList(event.target.value.split('\n').map((item) => item.trim()).filter(Boolean), field.listFormat || 'json'))}/><small>Escribe un elemento por línea.</small></label>
  }

  if (field.type === 'options') {
    const values = parseList(value)
    while (values.length < 4) values.push('')
    return <fieldset><legend>{field.label}{field.required && ' *'}{help}</legend>{values.slice(0, 4).map((option, index) => <label className="option-row" key={index}><span>{String.fromCharCode(65 + index)}</span><input value={option} disabled={disabled} onChange={(event) => { const next = [...values]; next[index] = event.target.value; update(encodeList(next)) }}/></label>)}</fieldset>
  }

  if (field.type === 'radio') {
    const radioChoices = choices.length ? choices : parseList(row.visual_options || row.options || '') .map((item) => ({ value: item, label: item }))
    return <fieldset><legend>{field.label}{field.required && ' *'}{help}</legend><div className="radio-stack">{radioChoices.map((choice) => <label key={choice.value}><input type="radio" name={field.key} value={choice.value} checked={value === choice.value} disabled={disabled} onChange={() => update(choice.value)}/><span>{choice.label}</span></label>)}</div></fieldset>
  }

  if (field.type === 'matching') {
    const entries = Object.entries(parseMap(value))
    while (entries.length < 4) entries.push(['', ''])
    return <fieldset><legend>{field.label}{field.required && ' *'}{help}</legend>{entries.map(([left, right], index) => <div className="pair-row" key={index}><input value={left} placeholder="Concepto o alternativa" disabled={disabled} onChange={(event) => { const next = [...entries]; next[index] = [event.target.value, right]; update(JSON.stringify(Object.fromEntries(next.filter(([a, b]) => a || b)))) }}/><input value={right} placeholder="Relación o retroalimentación" disabled={disabled} onChange={(event) => { const next = [...entries]; next[index] = [left, event.target.value]; update(JSON.stringify(Object.fromEntries(next.filter(([a, b]) => a || b)))) }}/></div>)}</fieldset>
  }

  if (field.type === 'matrix') {
    let display = ''
    try { display = (JSON.parse(value || '[]') as string[][]).map((cells) => cells.join(' | ')).join('\n') } catch { display = '' }
    return <label className="field"><span>{field.label}{help}</span><textarea value={display} rows={6} disabled={disabled} onChange={(event) => update(JSON.stringify(event.target.value.split('\n').filter(Boolean).map((line) => line.split('|').map((cell) => cell.trim()))))}/><small>Una fila por línea y columnas separadas por |.</small></label>
  }

  return <label htmlFor={id} className="field"><span>{field.label}{field.required && <b> *</b>}{help}</span><input id={id} value={value} onChange={(event) => update(event.target.value)} disabled={disabled} placeholder={field.placeholder}/></label>
}
